import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputLabel,
  Divider,
  Table,
  TableFooter,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { handleApiError } from '~/utils/helper';
import { ISourceReferralReport } from '~/utils/interface/report';
import { HeadCellType } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect } from '~/components/common';
import dayjs from 'dayjs';
import { handleGetSourceReferralReport } from '~/thunks/report/reportThunk';
import { handleGetSelectionCustomers } from '~/thunks/customer/customerThunk';

const SourceReferralReport = () => {
  const { t } = useTranslation('admin/report');
  const dispatch = useAppDispatch();

  const currentDate = new Date();
  const end = currentDate.toISOString().split('T')[0];
  const start = new Date(currentDate.setMonth(currentDate.getMonth() - 1)).toISOString().split('T')[0];
  const headCells: Array<HeadCellType> = useMemo(
    () => [
      { id: 'organization', label: t('properties.organization') },
      { id: 'type', label: t('properties.type') },
      { id: 'projectReferred', label: t('properties.projectReferred') },
    ],
    [t]
  );
  const { selectionCustomers } = useAppSelector((state) => state.customer);

  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  const [selectedAccount, setSelectedAccount] = useState<Array<string>>([]); // State for selected account
  const [report, setReport] = useState<Array<ISourceReferralReport>>([]);
  const optionsWithAll = [{ id: 'all', name: 'All' }, ...selectionCustomers];

  const handleSearch = () => {
    // Dispatch the request to fetch the activity report with the selected dates and account filter
    if (selectedAccount.includes('all')) {
      dispatch(handleGetSourceReferralReport({ startDate, endDate }))
        .unwrap()
        .then((response) => {
          setReport(response.result || []);
        })
        .catch(handleApiError);
    } else {
      dispatch(handleGetSourceReferralReport({ startDate, endDate, createdBy: selectedAccount ?? [] }))
        .unwrap()
        .then((response) => {
          setReport(response.result || []);
        })
        .catch(handleApiError);
    }
  };
  useEffect(() => {
    dispatch(handleGetSelectionCustomers({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetSourceReferralReport({ startDate: start, endDate: end }))
      .unwrap()
      .then((response) => {
        setStartDate(start);
        setEndDate(end);

        setSelectedAccount(['all']);
        setReport(response.result || []);
      })
      .catch(handleApiError);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <BaseHeaderTitle title={t('title.sourceReferral')} />

      <Divider className='mb-[20px] mt-[20px]' />
      <BaseRow className='gap-[20px] '>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={startDate}
            onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={endDate}
            onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.organization')}</InputLabel>
          <BaseSelect
            options={optionsWithAll}
            value={selectedAccount}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAccount.includes('all')) {
                setSelectedAccount(['all']);
              } else if (selectedValue !== 'all' && selectedAccount.includes('all')) {
                setSelectedAccount(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAccount(selectedValue);
              }
            }}
            label={t('properties.createdBy')}
            placeholder={t('properties.createdBy')}
            multiple
          />
        </Box>
        <Box className='w-[200px] mt-[12px]'>
          <InputLabel></InputLabel>
          <Button variant='contained' color='primary' onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </BaseRow>

      <Divider className='mb-[20px] mt-[20px]' />
      <TableContainer className='max-h-[640px] overflow-auto'>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell key={cell.id} className='w-[33.33%] bg-[#F0F7FF] text-[#758CA5] text-lg font-bold'>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {report.map((row, index) => (
              <TableRow key={index}>
                <TableCell className='w-[33.33%]'>{row.name}</TableCell>
                <TableCell className='w-[33.33%]'>{row.type}</TableCell>
                <TableCell className='w-[33.33%]'>{row.projectCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          {/* Total row */}
          <TableFooter>
            <TableRow className='sticky bottom-0 bg-[#F0F7FF]'>
              <TableCell className='w-[33.33%] font-bold text-[#758CA5]'>
                {report.filter((row) => row.id).length}
              </TableCell>
              <TableCell className='w-[33.33%] font-bold text-right text-[#758CA5]'></TableCell>
              <TableCell className='w-[33.33%] font-bold text-[#758CA5]'>
                {report.reduce((sum, row) => sum + (row.projectCount || 0), 0)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SourceReferralReport;
